import { useEffect, useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { activeWeekCount, type DayPanelComposedEntry, type DayPanelEntry } from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import { useArmedConfirm } from "../primitives/useArmedConfirm";
import { rangeLabel } from "../ProgramComposer/composeBlocks";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { makeDayPanelStyles } from "./dayPanelStyles";

// The selected day's exercises, grouped the way the program means them:
// one card per exercise (or superset), week-ramps compressed to one
// summary line per member (buildDayPanel). Read-only when no handlers
// are passed (Home preview); with handlers it is the composer's
// actionable preview — tap a card → selection border (never colour-only)
// + the anchored ✎/⧉/✕ bar. Remove is a two-step inline confirm (RN
// Alert is a no-op on react-native-web).

export type DayPanelProps = {
  entries: DayPanelEntry[];
  dayName: string;
  cycleWeeks: number;
  dark: boolean;
  resolveName?: (exerciseId: string, snapshot?: string) => string;
  onEdit?: (entry: DayPanelComposedEntry) => void;
  onClone?: (entry: DayPanelComposedEntry) => void;
  onRemove?: (entry: DayPanelEntry) => void;
};

const entryCellCount = (e: DayPanelEntry, cycleWeeks: number): number => {
  if (e.kind === "verbatim") return activeWeekCount(e.block.slot, cycleWeeks);
  return e.blocks.reduce(
    (acc, b) => acc + b.placements.length * b.weeks.length * b.members.length,
    0,
  );
};

export function DayPanel({
  entries,
  dayName,
  cycleWeeks,
  dark,
  resolveName,
  onEdit,
  onClone,
  onRemove,
}: DayPanelProps) {
  const { t } = useTranslation();
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const styles = useMemo(() => makeDayPanelStyles(theme), [theme]);
  const interactive = Boolean(onEdit || onClone || onRemove);
  const [selected, setSelected] = useState<number | null>(null);
  const [confirming, setConfirming] = useArmedConfirm();

  // Entries re-derive after every commit/remove — an index kept across
  // that would highlight the wrong card.
  useEffect(() => {
    setSelected(null);
    setConfirming(false);
    // `setConfirming` is the raw useState setter returned by
    // useArmedConfirm (primitives/useArmedConfirm.ts:15,21) — React
    // guarantees it stable, but eslint cannot see through the custom
    // hook. Pre-existing, surfaced when packages/ui got a linter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  const name = (id: string, snapshot?: string) =>
    resolveName ? resolveName(id, snapshot) : snapshot ?? id;

  if (entries.length === 0) {
    return (
      <View>
        <Text style={styles.dayName} numberOfLines={2}>{dayName}</Text>
        <Text style={styles.emptyText}>
          {t("mobile.preview.restDay", { defaultValue: "Rest / light" })}
        </Text>
      </View>
    );
  }

  return (
    <View>
      <Text style={styles.dayName} numberOfLines={2}>{dayName}</Text>
      {/* The tap-a-card affordance was invisible (Antenore 2026-06-11:
           "non è evidente che si tocca la card per modificare"). One quiet
           line, gone as soon as a card is selected — the anchored bar
           teaches the rest. */}
      {interactive && selected === null ? (
        <Text style={styles.panelHint}>
          {t("composer.panelHint", {
            defaultValue: "Tap an exercise to edit, clone or remove",
          })}
        </Text>
      ) : null}
      {entries.map((entry, idx) => {
        const isSel = interactive && selected === idx;
        const partialWeeks =
          entry.kind === "composed" && entry.weeks.length < cycleWeeks;
        return (
          <Pressable
            key={idx}
            accessibilityRole={interactive ? "button" : undefined}
            accessibilityState={interactive ? { selected: isSel } : undefined}
            disabled={!interactive}
            onPress={() => {
              setConfirming(false);
              setSelected((cur) => (cur === idx ? null : idx));
            }}
            style={[styles.entryCard, isSel && styles.entryCardSelected]}
          >
            {entry.kind === "verbatim" ? (
              <>
                <View style={styles.memberRow}>
                  <Text style={styles.memberName} numberOfLines={1}>
                    {name(entry.exerciseId, entry.exerciseName)}
                  </Text>
                </View>
                <Text style={styles.verbatimNote}>
                  {t("composer.verbatim", {
                    defaultValue:
                      "Advanced slot (multi-variant) — preserved as-is, not editable here",
                  })}
                </Text>
              </>
            ) : (
              <>
                {entry.members.map((m, i) => (
                  <View key={`${m.exerciseId}_${i}`} style={styles.memberRow}>
                    {entry.grouped ? (
                      <Text style={styles.memberOrdinal}>{`A${i + 1}`}</Text>
                    ) : null}
                    <Text style={styles.memberName} numberOfLines={1}>
                      {name(m.exerciseId, m.exerciseName)}
                    </Text>
                    {m.summary ? (
                      <Text style={styles.memberSummary}>{m.summary}</Text>
                    ) : null}
                  </View>
                ))}
                {partialWeeks ? (
                  <View style={styles.weeksChip}>
                    <Text style={styles.weeksChipText}>
                      {rangeLabel("W", entry.weeks)}
                    </Text>
                  </View>
                ) : null}
              </>
            )}

            {isSel ? (
              <View style={styles.actionsBar}>
                {entry.kind === "composed" && onEdit ? (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      setSelected(null);
                      onEdit(entry);
                    }}
                    style={styles.actionBtn}
                  >
                    <View style={{ flexDirection: "row", alignItems: "center", gap: theme.spacing.s2 }}>
                      <Icon name="edit" size={theme.typography.sizes.sm} color={theme.palette.ink} />
                      <Text style={styles.actionText}>{t("program.edit", { defaultValue: "Edit" })}</Text>
                    </View>
                  </Pressable>
                ) : null}
                {/* ⧉ only for uniform entries: the form holds ONE block,
                    cloning a whole ramp needs a model it doesn't have yet
                    (documented residual — clone per-segment via ✎ instead). */}
                {entry.kind === "composed" && entry.blocks.length === 1 && onClone ? (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      setSelected(null);
                      onClone(entry);
                    }}
                    style={styles.actionBtn}
                  >
                    <View style={{ flexDirection: "row", alignItems: "center", gap: theme.spacing.s2 }}>
                      <Icon name="copy" size={theme.typography.sizes.sm} color={theme.palette.ink} />
                      <Text style={styles.actionText}>{t("composer.clone", { defaultValue: "Clone" })}</Text>
                    </View>
                  </Pressable>
                ) : null}
                {onRemove ? (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      if (!confirming) {
                        setConfirming(true);
                        return;
                      }
                      setSelected(null);
                      setConfirming(false);
                      onRemove(entry);
                    }}
                    style={[styles.actionBtn, styles.actionBtnDanger]}
                  >
                    {confirming ? (
                      <Text style={[styles.actionText, styles.actionTextDanger]}>
                        {t("composer.removeConfirm", {
                          n: entryCellCount(entry, cycleWeeks),
                          defaultValue: "Confirm? ({{n}} cells)",
                        })}
                      </Text>
                    ) : (
                      <View style={{ flexDirection: "row", alignItems: "center", gap: theme.spacing.s2 }}>
                        <Icon name="close" size={theme.typography.sizes.sm} color={theme.palette.danger} />
                        <Text style={[styles.actionText, styles.actionTextDanger]}>{t("composer.remove", { defaultValue: "Remove" })}</Text>
                      </View>
                    )}
                  </Pressable>
                ) : null}
              </View>
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
}
